import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { NavLayoutService } from '@shared/services/nav-layout.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { SidebarDTO, SidebarGroupDTO, SidebarCategoryDTO, SidebarModuleDTO } from '@models/sidebar.model';
import { CatalogLabelService } from '@shared/services/i18n/catalog-label.service';
import { SidebarSearchService, SidebarSearchResult, HighlightPart } from '@shared/services/navigation/sidebar-search.service';
import { BadgeComponent } from '@shared/components/ui/badge/badge.component';
import { ThemeService } from '@shared/services/theme.service';

/**
 * Barra de navegación horizontal (mega-menú) que se muestra bajo el header cuando el usuario
 * elige la disposición `topnav` en Configuración › Apariencia. Alternativa al menú lateral.
 *
 * Renderiza los 3 niveles del catálogo — grupo (fila superior) → categoría (columnas del panel)
 * → módulo (enlaces) — reutilizando exactamente la misma fuente que el sidebar lateral
 * ({@link PermissionService.getSidebarForRoleCached}, ya filtrada por permiso y plan en el
 * backend) y el mismo {@link CatalogLabelService} para las etiquetas i18n. Solo se muestra en
 * pantallas anchas (el layout la oculta bajo `xl`, donde sigue usándose el cajón lateral).
 */
@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslocoModule, BadgeComponent],
  templateUrl: './app-top-nav.component.html',
  styleUrl: './app-top-nav.component.css',
  // Fondo del host aplicado INLINE (máxima prioridad): cubre TODO lo que va en flujo dentro del
  // topnav — la barra de grupos y el mega-menú FIJADO (`relative`) — sin depender de clases CSS
  // ni del orden de capas de Tailwind. Los paneles que FLOTAN (mega-menú sin fijar + buscador)
  // llevan su propio fondo inline / `tn-surface`.
  host: {
    '[style.backgroundColor]': 'surfaceColor()',
  },
})
export class AppTopNavComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly usuarioService = inject(UsuarioService);
  private readonly permissionService = inject(PermissionService);
  private readonly navLayout = inject(NavLayoutService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly catalogLabel = inject(CatalogLabelService);
  private readonly sidebarSearch = inject(SidebarSearchService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly theme = inject(ThemeService);

  readonly sidebarData = signal<SidebarDTO | null>(null);

  /**
   * Color de fondo del mega-menú aplicado INLINE (máxima prioridad). El mega-menú FIJADO va en
   * flujo (`relative`) y ahí la clase de fondo del componente no llegaba a pintarlo (quedaba
   * transparente sobre el contenido). Un estilo inline theme-aware garantiza el fondo pase lo que
   * pase con la encapsulación emulada o el orden de capas de Tailwind. #101828 = gray-900.
   */
  readonly surfaceColor = computed(() => (this.theme.theme() === 'dark' ? '#101828' : '#ffffff'));

  // ── Buscador de la barra superior ───────────────────────────────────────────
  // Mismo comportamiento que el buscador del menú lateral (lógica compartida en
  // SidebarSearchService), presentado como un desplegable de resultados bajo la barra.
  readonly searchQuery = signal('');
  readonly activeResultIndex = signal(0);
  @ViewChild('topNavSearchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  /** Última lista de resultados renderizada; la reusan los handlers de teclado. */
  private lastResults: SidebarSearchResult[] = [];
  /** Pista del atajo según la plataforma (⌘K en Mac, Ctrl K en el resto). */
  readonly shortcutHint = /mac|iphone|ipad/i.test(navigator.userAgent) ? '⌘ K' : 'Ctrl K';

  /** Id del grupo cuyo mega-menú está desplegado; `null` = todo cerrado. */
  readonly openGroupId = signal<number | null>(null);

  /**
   * Mega-menú fijado (preferencia persistida). Al fijar, el panel del grupo abierto queda
   * estático en flujo (empuja el contenido hacia abajo) y no se cierra al navegar / clicar
   * fuera. Solo aplica a la barra superior (este componente únicamente existe en `topnav`).
   */
  readonly pinned = this.navLayout.topNavPinned;

  /** Grupo abierto resuelto contra el árbol actual (o `null`). */
  readonly openGroup = computed<SidebarGroupDTO | null>(() => {
    const id = this.openGroupId();
    if (id === null) return null;
    return this.sidebarData()?.groups.find(g => g.id === id) ?? null;
  });

  private lastCompanyId: number | null = null;
  private lastRoleId: number | null = null;
  private readonly subscription = new Subscription();

  constructor() {
    // Carga/recarga del árbol al cambiar el perfil o la empresa activa — misma estrategia que el
    // sidebar lateral: solo invalida la caché cuando role/empresa cambian respecto a la última
    // carga, evitando HTTP duplicados en el arranque (F5). `untracked` porque clearSidebarCache
    // lee/escribe el signal de caché y, sin él, el effect entraría en bucle.
    effect(() => {
      const perfil = this.usuarioService.perfil();
      const companyId = this.companyContext.companyId();
      if (perfil?.role?.id && companyId !== null) {
        const roleId = perfil.role.id;
        untracked(() => {
          const changed = companyId !== this.lastCompanyId || roleId !== this.lastRoleId;
          if (changed) {
            this.permissionService.clearSidebarCache(roleId);
            this.lastCompanyId = companyId;
            this.lastRoleId = roleId;
          }
          this.load(roleId);
        });
      }
    });

    // Fijado: garantizar que SIEMPRE haya un panel abierto mientras esté fijado (al fijar, al
    // recargar con la preferencia activa, o si el árbol carga después). Abre el grupo de la ruta
    // actual y, si no hay, el primero. Se lee `openGroupId` para reaccionar cuando queda en null.
    effect(() => {
      if (!this.pinned()) return;
      const data = this.sidebarData();
      if (!data || data.groups.length === 0 || this.openGroupId() !== null) return;
      untracked(() => {
        const active = data.groups.find(g => this.isGroupActive(g));
        this.openGroupId.set((active ?? data.groups[0]).id);
      });
    });
  }

  ngOnInit(): void {
    // Cerrar el mega-menú al navegar (clic en un módulo o navegación externa),
    // salvo cuando está fijado: ahí permanece estático abierto.
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd && !this.pinned()) {
          this.openGroupId.set(null);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private load(roleId: number): void {
    this.permissionService.getSidebarForRoleCached(roleId).subscribe({
      next: (data) => {
        this.sidebarData.set(data);
        this.cdr.detectChanges();
      },
      // El errorInterceptor ya avisa; la barra queda con lo último cargado.
      error: () => undefined,
    });
  }

  // ── Interacción ─────────────────────────────────────────────────────────────
  toggleGroup(id: number): void {
    // Fijado: cambiar de grupo mantiene siempre un panel abierto (nunca a null),
    // para que el contenido permanezca desplazado y el menú estático.
    if (this.pinned()) {
      this.openGroupId.set(id);
      return;
    }
    this.openGroupId.update(cur => (cur === id ? null : id));
  }

  /** Alterna el fijado del mega-menú. Al fijar abre el panel del grupo activo (o el primero);
   *  al desfijar lo cierra para que el contenido vuelva a su posición. */
  togglePinned(): void {
    this.navLayout.toggleTopNavPin();
    if (!this.pinned()) {
      this.openGroupId.set(null);
    }
    // Si quedó fijado, el effect del constructor abre el panel adecuado.
  }

  closeMenu(): void {
    // Con el menú fijado no se cierra al elegir un módulo: permanece estático abierto.
    if (this.pinned()) return;
    this.openGroupId.set(null);
  }

  /** Cierra el mega-menú y el desplegable de búsqueda al hacer clic fuera de la barra. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.host.nativeElement.contains(event.target as Node)) return;
    if (this.isSearching()) this.clearSearch();
    // El mega-menú fijado permanece estático abierto.
    if (!this.pinned() && this.openGroupId() !== null) {
      this.openGroupId.set(null);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.pinned()) return;
    this.openGroupId.set(null);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  isActive(url: string): boolean {
    return this.router.isActive(url, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  /** Un grupo está activo si alguno de sus módulos coincide con la ruta actual. */
  isGroupActive(g: SidebarGroupDTO): boolean {
    return g.categories.some(c => c.modules.some(m => this.isActive(m.url)));
  }

  // i18n de etiquetas del catálogo — misma resolución que el sidebar lateral.
  groupLabel(g: SidebarGroupDTO): string {
    return this.catalogLabel.group(g.name);
  }

  categoryLabel(c: SidebarCategoryDTO): string {
    return this.catalogLabel.category(c.name, c.url);
  }

  moduleLabel(m: SidebarModuleDTO): string {
    return this.catalogLabel.module(m.name, m.url);
  }

  // ── Buscador ─────────────────────────────────────────────────────────────────
  isSearching(): boolean {
    return this.searchQuery().trim().length > 0;
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.activeResultIndex.set(0);
    // Al buscar, cerrar cualquier mega-menú abierto para no solaparlo con el desplegable
    // de resultados (salvo fijado, que permanece en flujo empujando el contenido).
    if (!this.pinned()) this.openGroupId.set(null);
  }

  /** Resetea el estado de búsqueda (sin tocar el foco). */
  clearSearch(): void {
    this.searchQuery.set('');
    this.activeResultIndex.set(0);
    this.lastResults = [];
  }

  /** Limpia y devuelve el foco al input (botón «×» y tecla Esc). */
  clearSearchAndFocus(): void {
    this.clearSearch();
    this.searchInputRef?.nativeElement.focus();
  }

  /** Módulos coincidentes (lógica compartida con el menú lateral). */
  searchResults(): SidebarSearchResult[] {
    this.lastResults = this.sidebarSearch.search(this.sidebarData(), this.searchQuery());
    return this.lastResults;
  }

  /** Segmentos {texto, match} para resaltar la porción coincidente de una etiqueta. */
  highlightParts(text: string): HighlightPart[] {
    return this.sidebarSearch.highlightParts(text, this.searchQuery());
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const results = this.lastResults;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (results.length) {
          this.activeResultIndex.update(i => Math.min(i + 1, results.length - 1));
          this.scrollActiveResultIntoView();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (results.length) {
          this.activeResultIndex.update(i => Math.max(i - 1, 0));
          this.scrollActiveResultIntoView();
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const selected = results[this.activeResultIndex()];
        if (selected) this.goToResult(selected);
        break;
      }
      case 'Escape':
        // Evitar que el HostListener de Escape cierre además el mega-menú.
        event.stopPropagation();
        event.preventDefault();
        this.clearSearchAndFocus();
        break;
    }
  }

  /** Clic sobre un resultado: el [routerLink] ya navega; aquí solo limpiamos la búsqueda. */
  onResultClick(): void {
    this.clearSearch();
  }

  /** Navegación por teclado (Enter): navega explícitamente y limpia la búsqueda. */
  goToResult(result: SidebarSearchResult): void {
    this.router.navigateByUrl(result.module.url);
    this.clearSearch();
  }

  private scrollActiveResultIntoView(): void {
    setTimeout(() => {
      document
        .getElementById('tn-search-result-' + this.activeResultIndex())
        ?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  /**
   * Atajo Ctrl/⌘+K: enfoca el buscador de la barra superior. Solo se maneja aquí cuando la
   * barra es la superficie de navegación activa (escritorio + disposición 'topnav'); en el
   * resto de casos lo gestiona el menú lateral, evitando doble manejo del atajo.
   */
  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if (!((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K'))) return;
    if (this.navLayout.mode() !== 'topnav' || window.innerWidth < 1280) return;
    event.preventDefault();
    setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
  }
}
